/**
 * 콘솔 부트스트랩.
 *
 * 설정은 세 곳에서 온다. 우선순위 순:
 *   1. 호스트가 주입한 전역(`window.__SIGNAGEONE_CONFIG__`) — 플러그인 번들·서버 렌더
 *   2. URL 쿼리(`?server=&token=&parent=`) — iframe 임베드
 *   3. 빌드 기본값 — 독립 웹 배포
 *
 * 배포 산출물은 **하나**다. 어디에 놓이느냐에 따라 스스로 통합 방식을 정한다.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { createTransport } from './api/transport.js';
import './styles.css';

interface InjectedConfig {
  baseUrl?: string;
  token?: string;
  parentOrigin?: string;
}

function readConfig(): { baseUrl: string; token: string | null; parentOrigin?: string } {
  const injected = (window as { __SIGNAGEONE_CONFIG__?: InjectedConfig }).__SIGNAGEONE_CONFIG__ ?? {};
  const params = new URLSearchParams(window.location.search);

  const baseUrl = injected.baseUrl ?? params.get('server') ?? window.location.origin;
  const token = injected.token ?? params.get('token');
  const parentOrigin = injected.parentOrigin ?? params.get('parent') ?? undefined;

  // 토큰이 URL 에 있으면 주소창·기록에서 지운다. 남겨두면 어깨너머·공유로 새어나간다.
  //
  // 해시는 반드시 같이 남긴다 — 라우팅이 해시에 있어서, 지우면 `?token=…#/content`
  // 같은 링크가 항상 대시보드로 떨어진다.
  if (params.has('token')) {
    params.delete('token');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  }

  return { baseUrl: baseUrl.replace(/\/+$/, ''), token, parentOrigin };
}

function main(): void {
  const config = readConfig();
  // 업로드는 multipart 라 전송 계층을 우회한다 — 토큰을 그 경로에만 노출한다.
  if (config.token) {
    (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__ = config.token;
  }
  // 서버가 경로로만 주는 주소(올린 HTML 미리보기)를 서버 기준으로 연다 — 콘솔이 다른 출처에서 뜰 때도.
  (globalThis as { __SIGNAGEONE_BASE__?: string }).__SIGNAGEONE_BASE__ = config.baseUrl;

  const transport = createTransport(config);
  const root = document.getElementById('root');
  if (!root) throw new Error('#root 를 찾을 수 없습니다');

  createRoot(root).render(
    <StrictMode>
      <App transport={transport} />
    </StrictMode>,
  );
}

main();
